import { SupportedLanguage } from "@/components/multilingual/LanguageSelector";

export interface RefundPolicyTranslation {
  title: string;
  breadcrumb: string;
  lastUpdatedLabel: string;
  introduction: {
    title: string;
    content: string;
  };
  paymentProcessor: {
    title: string;
    content: string;
  };
  withdrawalRight: {
    title: string;
    content: string;
    linkText: string;
  };
  digitalContentWaiver: {
    title: string;
    content: string;
  };
  refundsAvailable: {
    title: string;
    intro: string;
    items: string[];
  };
  refundsNotAvailable: {
    title: string;
    intro: string;
    items: string[];
  };
  howToRequest: {
    title: string;
    content: string;
    paddleLabel: string;
    paddleUrl: string;
    emailLabel: string;
    email: string;
    noteContent: string;
  };
  processingTimes: {
    title: string;
    intro: string;
    items: string[];
    note: string;
  };
  chargebackNotice: {
    title: string;
    content: string;
  };
}

const controllerInfo = {
  email: "contact@paperverse.io",
};

export const refundPolicyTranslations: Record<SupportedLanguage, RefundPolicyTranslation> = {
  de: {
    title: "Erstattungsrichtlinie",
    breadcrumb: "Erstattungsrichtlinie",
    lastUpdatedLabel: "Zuletzt aktualisiert",
    introduction: {
      title: "1. Geltungsbereich",
      content: "Diese Erstattungsrichtlinie gilt für alle kostenpflichtigen Leistungen auf PaperVerse: monatliche Credit-Abonnements (Pläne) sowie einmalige Credit-Pakete (Top-ups). Credits werden sowohl für KI-Reviews als auch für PaperVerse Writer verbraucht. Ein Abonnement können Sie jederzeit zum Ende der laufenden Abrechnungsperiode kündigen (u. a. über die Kündigungsschaltfläche unter /cancel-contract); die Kündigung verhindert künftige Verlängerungen, führt aber nicht automatisch zur Erstattung der bereits laufenden Periode.",
    },
    paymentProcessor: {
      title: "2. Zahlungsabwicklung (Merchant of Record)",
      content: "Die Zahlungsabwicklung für alle Käufe erfolgt durch Paddle.com Market Limited als Reseller (\"Merchant of Record\"). Paddle ist Ihr direkter Vertragspartner für alle Zahlungsvorgänge und verantwortlich für die Abwicklung von Erstattungen.",
    },
    withdrawalRight: {
      title: "3. Ihr Widerrufsrecht",
      content: "Als Verbraucher in der Europäischen Union haben Sie grundsätzlich ein gesetzliches 14-tägiges Widerrufsrecht. Vollständige Informationen zu diesem Recht, einschließlich der Fristen und des Muster-Widerrufsformulars, finden Sie in unserer",
      linkText: "Widerrufsbelehrung",
    },
    digitalContentWaiver: {
      title: "4. Besonderheit bei digitalen Inhalten",
      content: "KI-Review-Credits sind digitale Inhalte, die sofort nach Kauf in Ihrem Konto verfügbar sind. Gemäß § 356 Abs. 5 BGB erlischt das Widerrufsrecht bei einem Vertrag über die Lieferung von nicht auf einem körperlichen Datenträger befindlichen digitalen Inhalten, sobald der Unternehmer mit der Ausführung des Vertrags begonnen hat, nachdem der Verbraucher ausdrücklich zugestimmt hat, dass der Unternehmer mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist beginnt, und seine Kenntnis davon bestätigt hat, dass er durch seine Zustimmung mit Beginn der Ausführung des Vertrags sein Widerrufsrecht verliert. Da Sie dieser Bedingung vor dem Kauf zugestimmt haben, ist eine Erstattung nach diesem Zeitpunkt grundsätzlich ausgeschlossen.",
    },
    refundsAvailable: {
      title: "5. Wann Erstattungen möglich sind",
      intro: "Ungeachtet des Erlöschens des Widerrufsrechts können Sie in folgenden Fällen eine Erstattung beantragen:",
      items: [
        "Die erworbenen Credits sind aufgrund technischer Fehler auf unserer Seite nicht verfügbar oder nutzbar",
        "Eine doppelte Abbuchung für dieselbe Transaktion erfolgte",
        "Der Kauf wurde nachweislich nicht von Ihnen autorisiert (Betrug)",
        "Die Credits wurden Ihrem Konto nach dem Kauf nicht gutgeschrieben",
      ],
    },
    refundsNotAvailable: {
      title: "6. Wann keine Erstattung möglich ist",
      intro: "Eine Erstattung ist in folgenden Fällen nicht möglich:",
      items: [
        "Nach Nutzung eines oder mehrerer Credits (teilweise Erstattung für ungenutzte Credits ist ausgeschlossen)",
        "Bei einfacher Meinungsänderung nach dem Kauf",
        "Wenn das Widerrufsrecht gemäß Abschnitt 4 erloschen ist und kein technischer Fehler vorliegt",
        "Wenn Sie mit dem Ergebnis einer KI-Review unzufrieden sind (KI-Ergebnisse können variieren)",
      ],
    },
    howToRequest: {
      title: "7. So beantragen Sie eine Erstattung",
      content: "Für Erstattungsanfragen stehen Ihnen folgende Kontaktmöglichkeiten zur Verfügung:",
      paddleLabel: "Paddle Buyer Support",
      paddleUrl: "https://paddle.net",
      emailLabel: "PaperVerse Support",
      email: controllerInfo.email,
      noteContent: "Bitte geben Sie bei Ihrer Anfrage die Bestellnummer (Transaktions-ID), die mit dem Kauf verknüpfte E-Mail-Adresse und eine Beschreibung des Problems an.",
    },
    processingTimes: {
      title: "8. Bearbeitungszeiten",
      intro: "Nach Genehmigung einer Erstattung gelten folgende Bearbeitungszeiten:",
      items: [
        "Kreditkarte: 3-5 Werktage",
        "PayPal: bis zu 48 Stunden",
        "Andere Zahlungsmethoden: abhängig vom Zahlungsanbieter",
      ],
      note: "Die Erstattung erfolgt über dieselbe Zahlungsmethode, die für den ursprünglichen Kauf verwendet wurde.",
    },
    chargebackNotice: {
      title: "9. Hinweis zu Chargebacks",
      content: "Bitte kontaktieren Sie uns oder Paddle, bevor Sie eine Rückbuchung (Chargeback) bei Ihrer Bank oder Ihrem Kreditkartenunternehmen beantragen. Unbegründete Chargebacks können zur Sperrung Ihres Kontos führen und verursachen unnötige Kosten für alle Beteiligten. Wir sind bestrebt, alle berechtigten Erstattungsanfragen schnell und fair zu bearbeiten.",
    },
  },
  en: {
    title: "Refund Policy",
    breadcrumb: "Refund Policy",
    lastUpdatedLabel: "Last updated",
    introduction: {
      title: "1. Scope",
      content: "This refund policy applies to all paid services on PaperVerse: monthly credit subscriptions (plans) and one-off credit top-up packs. Credits are consumed by both AI reviews and PaperVerse Writer. You can cancel a subscription at any time with effect from the end of the current billing period (including via the cancellation button at /cancel-contract); cancellation stops future renewals but does not automatically refund the period already running.",
    },
    paymentProcessor: {
      title: "2. Payment Processing (Merchant of Record)",
      content: "Payment processing for all purchases is handled by Paddle.com Market Limited as the reseller (\"Merchant of Record\"). Paddle is your direct contractual partner for all payment transactions and is responsible for processing refunds.",
    },
    withdrawalRight: {
      title: "3. Your Right of Withdrawal",
      content: "As a consumer in the European Union, you generally have a statutory 14-day right of withdrawal. Complete information about this right, including deadlines and the model withdrawal form, can be found in our",
      linkText: "Cancellation Policy",
    },
    digitalContentWaiver: {
      title: "4. Special Provisions for Digital Content",
      content: "AI review credits are digital content that becomes available in your account immediately after purchase. Pursuant to Section 356(5) of the German Civil Code (BGB), the right of withdrawal expires for contracts for the supply of digital content not supplied on a tangible medium if performance has begun after the consumer has given their express consent to the entrepreneur beginning performance before expiry of the withdrawal period and has confirmed their acknowledgment that they will lose their right of withdrawal upon commencement of performance. As you have agreed to this condition before purchase, a refund is generally excluded after this point.",
    },
    refundsAvailable: {
      title: "5. When Refunds Are Available",
      intro: "Notwithstanding the expiration of the right of withdrawal, you may request a refund in the following cases:",
      items: [
        "The purchased credits are unavailable or unusable due to technical errors on our side",
        "A duplicate charge occurred for the same transaction",
        "The purchase was demonstrably not authorized by you (fraud)",
        "The credits were not credited to your account after purchase",
      ],
    },
    refundsNotAvailable: {
      title: "6. When Refunds Are Not Available",
      intro: "A refund is not possible in the following cases:",
      items: [
        "After using one or more credits (partial refunds for unused credits are excluded)",
        "In case of simple change of mind after purchase",
        "If the right of withdrawal has expired pursuant to Section 4 and no technical error exists",
        "If you are dissatisfied with the result of an AI review (AI results may vary)",
      ],
    },
    howToRequest: {
      title: "7. How to Request a Refund",
      content: "For refund requests, the following contact options are available:",
      paddleLabel: "Paddle Buyer Support",
      paddleUrl: "https://paddle.net",
      emailLabel: "PaperVerse Support",
      email: controllerInfo.email,
      noteContent: "Please include in your request the order number (transaction ID), the email address associated with the purchase, and a description of the issue.",
    },
    processingTimes: {
      title: "8. Processing Times",
      intro: "After approval of a refund, the following processing times apply:",
      items: [
        "Credit card: 3-5 business days",
        "PayPal: up to 48 hours",
        "Other payment methods: depending on the payment provider",
      ],
      note: "Refunds are processed using the same payment method that was used for the original purchase.",
    },
    chargebackNotice: {
      title: "9. Note on Chargebacks",
      content: "Please contact us or Paddle before requesting a chargeback from your bank or credit card company. Unjustified chargebacks may result in the suspension of your account and cause unnecessary costs for all parties involved. We are committed to processing all legitimate refund requests quickly and fairly.",
    },
  },
  fr: {
    title: "Politique de Remboursement",
    breadcrumb: "Politique de Remboursement",
    lastUpdatedLabel: "Dernière mise à jour",
    introduction: {
      title: "1. Champ d'application",
      content: "Cette politique de remboursement s'applique à tous les services payants de PaperVerse : abonnements mensuels de crédits (forfaits) et packs de crédits ponctuels. Les crédits sont consommés à la fois par les revues IA et par PaperVerse Writer. Vous pouvez résilier un abonnement à tout moment avec effet à la fin de la période de facturation en cours (notamment via le bouton de résiliation à l'adresse /cancel-contract) ; la résiliation empêche les renouvellements futurs mais n'entraîne pas automatiquement le remboursement de la période en cours.",
    },
    paymentProcessor: {
      title: "2. Traitement des Paiements (Revendeur)",
      content: "Le traitement des paiements pour tous les achats est effectué par Paddle.com Market Limited en tant que revendeur (\"Merchant of Record\"). Paddle est votre partenaire contractuel direct pour toutes les transactions de paiement et est responsable du traitement des remboursements.",
    },
    withdrawalRight: {
      title: "3. Votre Droit de Rétractation",
      content: "En tant que consommateur dans l'Union européenne, vous disposez généralement d'un droit de rétractation légal de 14 jours. Les informations complètes sur ce droit, y compris les délais et le formulaire type de rétractation, se trouvent dans notre",
      linkText: "Politique d'Annulation",
    },
    digitalContentWaiver: {
      title: "4. Dispositions Spéciales pour le Contenu Numérique",
      content: "Les crédits de revue IA sont du contenu numérique qui devient disponible sur votre compte immédiatement après l'achat. Conformément à l'article 356(5) du Code civil allemand (BGB), le droit de rétractation expire pour les contrats de fourniture de contenu numérique non fourni sur un support matériel si l'exécution a commencé après que le consommateur a donné son consentement exprès à ce que l'entrepreneur commence l'exécution avant l'expiration du délai de rétractation et a confirmé qu'il reconnaît perdre son droit de rétractation au commencement de l'exécution. Comme vous avez accepté cette condition avant l'achat, un remboursement est généralement exclu après ce point.",
    },
    refundsAvailable: {
      title: "5. Quand les Remboursements Sont Disponibles",
      intro: "Nonobstant l'expiration du droit de rétractation, vous pouvez demander un remboursement dans les cas suivants :",
      items: [
        "Les crédits achetés sont indisponibles ou inutilisables en raison d'erreurs techniques de notre côté",
        "Un double prélèvement a eu lieu pour la même transaction",
        "L'achat n'a manifestement pas été autorisé par vous (fraude)",
        "Les crédits n'ont pas été crédités sur votre compte après l'achat",
      ],
    },
    refundsNotAvailable: {
      title: "6. Quand les Remboursements Ne Sont Pas Disponibles",
      intro: "Un remboursement n'est pas possible dans les cas suivants :",
      items: [
        "Après utilisation d'un ou plusieurs crédits (les remboursements partiels pour crédits non utilisés sont exclus)",
        "En cas de simple changement d'avis après l'achat",
        "Si le droit de rétractation a expiré conformément à la Section 4 et qu'aucune erreur technique n'existe",
        "Si vous êtes insatisfait du résultat d'une revue IA (les résultats IA peuvent varier)",
      ],
    },
    howToRequest: {
      title: "7. Comment Demander un Remboursement",
      content: "Pour les demandes de remboursement, les options de contact suivantes sont disponibles :",
      paddleLabel: "Support Acheteur Paddle",
      paddleUrl: "https://paddle.net",
      emailLabel: "Support PaperVerse",
      email: controllerInfo.email,
      noteContent: "Veuillez inclure dans votre demande le numéro de commande (ID de transaction), l'adresse e-mail associée à l'achat et une description du problème.",
    },
    processingTimes: {
      title: "8. Délais de Traitement",
      intro: "Après approbation d'un remboursement, les délais de traitement suivants s'appliquent :",
      items: [
        "Carte de crédit : 3-5 jours ouvrables",
        "PayPal : jusqu'à 48 heures",
        "Autres méthodes de paiement : selon le fournisseur de paiement",
      ],
      note: "Les remboursements sont traités en utilisant le même mode de paiement que celui utilisé pour l'achat initial.",
    },
    chargebackNotice: {
      title: "9. Note sur les Rétrofacturations",
      content: "Veuillez nous contacter ou contacter Paddle avant de demander une rétrofacturation à votre banque ou société de carte de crédit. Les rétrofacturations injustifiées peuvent entraîner la suspension de votre compte et causer des coûts inutiles pour toutes les parties concernées. Nous nous engageons à traiter toutes les demandes de remboursement légitimes rapidement et équitablement.",
    },
  },
  es: {
    title: "Política de Reembolso",
    breadcrumb: "Política de Reembolso",
    lastUpdatedLabel: "Última actualización",
    introduction: {
      title: "1. Ámbito de Aplicación",
      content: "Esta política de reembolso se aplica a todos los servicios de pago de PaperVerse: suscripciones mensuales de créditos (planes) y paquetes puntuales de créditos. Los créditos se consumen tanto en las revisiones de IA como en PaperVerse Writer. Puede cancelar una suscripción en cualquier momento con efectos al final del período de facturación en curso (también mediante el botón de cancelación en /cancel-contract); la cancelación evita futuras renovaciones, pero no reembolsa automáticamente el período ya iniciado.",
    },
    paymentProcessor: {
      title: "2. Procesamiento de Pagos (Comerciante Registrado)",
      content: "El procesamiento de pagos para todas las compras es manejado por Paddle.com Market Limited como revendedor (\"Merchant of Record\"). Paddle es su socio contractual directo para todas las transacciones de pago y es responsable del procesamiento de reembolsos.",
    },
    withdrawalRight: {
      title: "3. Su Derecho de Desistimiento",
      content: "Como consumidor en la Unión Europea, generalmente tiene un derecho legal de desistimiento de 14 días. La información completa sobre este derecho, incluyendo plazos y el formulario modelo de desistimiento, se puede encontrar en nuestra",
      linkText: "Política de Cancelación",
    },
    digitalContentWaiver: {
      title: "4. Disposiciones Especiales para Contenido Digital",
      content: "Los créditos de revisión IA son contenido digital que está disponible en su cuenta inmediatamente después de la compra. De conformidad con el artículo 356(5) del Código Civil alemán (BGB), el derecho de desistimiento expira para los contratos de suministro de contenido digital no suministrado en un soporte material si la ejecución ha comenzado después de que el consumidor haya dado su consentimiento expreso a que el empresario comience la ejecución antes de que expire el período de desistimiento y haya confirmado su conocimiento de que perderá su derecho de desistimiento al comenzar la ejecución. Como usted ha aceptado esta condición antes de la compra, un reembolso queda generalmente excluido después de este punto.",
    },
    refundsAvailable: {
      title: "5. Cuándo los Reembolsos Están Disponibles",
      intro: "Sin perjuicio de la expiración del derecho de desistimiento, puede solicitar un reembolso en los siguientes casos:",
      items: [
        "Los créditos comprados no están disponibles o no son utilizables debido a errores técnicos de nuestra parte",
        "Se produjo un cargo duplicado por la misma transacción",
        "La compra no fue autorizada por usted de manera demostrable (fraude)",
        "Los créditos no fueron acreditados a su cuenta después de la compra",
      ],
    },
    refundsNotAvailable: {
      title: "6. Cuándo los Reembolsos No Están Disponibles",
      intro: "Un reembolso no es posible en los siguientes casos:",
      items: [
        "Después de usar uno o más créditos (los reembolsos parciales por créditos no utilizados están excluidos)",
        "En caso de simple cambio de opinión después de la compra",
        "Si el derecho de desistimiento ha expirado según la Sección 4 y no existe ningún error técnico",
        "Si está insatisfecho con el resultado de una revisión IA (los resultados de IA pueden variar)",
      ],
    },
    howToRequest: {
      title: "7. Cómo Solicitar un Reembolso",
      content: "Para solicitudes de reembolso, están disponibles las siguientes opciones de contacto:",
      paddleLabel: "Soporte al Comprador de Paddle",
      paddleUrl: "https://paddle.net",
      emailLabel: "Soporte de PaperVerse",
      email: controllerInfo.email,
      noteContent: "Por favor incluya en su solicitud el número de pedido (ID de transacción), la dirección de correo electrónico asociada con la compra y una descripción del problema.",
    },
    processingTimes: {
      title: "8. Tiempos de Procesamiento",
      intro: "Después de la aprobación de un reembolso, se aplican los siguientes tiempos de procesamiento:",
      items: [
        "Tarjeta de crédito: 3-5 días hábiles",
        "PayPal: hasta 48 horas",
        "Otros métodos de pago: dependiendo del proveedor de pago",
      ],
      note: "Los reembolsos se procesan utilizando el mismo método de pago que se utilizó para la compra original.",
    },
    chargebackNotice: {
      title: "9. Nota sobre Contracargos",
      content: "Por favor contáctenos a nosotros o a Paddle antes de solicitar un contracargo a su banco o compañía de tarjeta de crédito. Los contracargos injustificados pueden resultar en la suspensión de su cuenta y causar costos innecesarios para todas las partes involucradas. Estamos comprometidos a procesar todas las solicitudes de reembolso legítimas de manera rápida y justa.",
    },
  },
};
